import { supabase } from "@/api/supabaseClient";
import type { Database } from "@/types/database";

type DeathCause = Database["public"]["Enums"]["death_cause"];
type DeathCauseNote =
  Database["public"]["Tables"]["care_event_closure_deceased"]["Row"]["death_cause_note"];

export type CareEventType =
  | "weight"
  | "free_text"
  | "diagnosis_change"
  | "place_change"
  | "status_change"
  | "closure_released"
  | "closure_deceased"
  | "intake_snapshot"
  | "image_ref"
  | "medication_dose"
  | "treatment"
  | "examination"
  | "plan_change"
  | "care_hint_change"
  | "care_site_change";

export type RhythmKind = "interval" | "daily_times" | "once" | "as_needed";

export type DiagnosisEpisodeState =
  | "suspicion"
  | "confirmed"
  | "ruled_out"
  | "in_treatment"
  | "closed";

export type CareEventInput = {
  event_type: CareEventType;
  sort_index?: number;
  client_event_id?: string;
  weight_grams?: number;
  body?: string;
  diagnosis_id?: string;
  to_state?: DiagnosisEpisodeState;
  to_place_id?: string;
  expected_from_place_id?: string | null;
  expected_from_state?: DiagnosisEpisodeState | null;
  to_status?: "in_care" | "hibernating";
  release_kind?: string;
  release_place_text?: string;
  death_kind?: string;
  cause_text?: string;
  died_at?: string | null;
  death_cause?: DeathCause;
  /** Freitext nur bei death_cause other. */
  death_cause_note?: DeathCauseNote;
  notes?: string | null;
  media_asset_id?: string;
  /** P5 therapy */
  medication_id?: string;
  treatment_id?: string;
  free_name?: string;
  amount?: number;
  unit?: string;
  route?: string;
  note?: string;
  plan_id?: string;
  plan_revision_id?: string;
  occurrence_key?: string;
  planned_at?: string;
  is_additional?: boolean;
  additional_reason?: string;
  packaging_photo_media_id?: string;
  exam_kind?: string;
  action?: "open" | "change" | "end";
  rhythm_kind?: RhythmKind;
  rhythm?: Record<string, unknown>;
  rhythm_note?: string;
  dose_amount?: number;
  dose_unit?: string;
  dose_text?: string;
  started_at?: string;
  planned_end_at?: string | null;
  diagnosis_episode_id?: string;
  end_status?: "completed" | "cancelled";
  hint_id?: string;
  is_warning?: boolean;
};

export type CareEntryView = {
  id: string;
  case_id: string;
  author_id: string;
  recorded_at: string;
  received_at: string;
  is_erroneous: boolean;
  erroneous_reason: string | null;
  client_entry_id: string | null;
  events: Array<Record<string, unknown>>;
};

export type PublicCareSummary = {
  case_id: string;
  current_weight_grams: number | null;
  active_diagnoses: Array<{
    episode_id: string;
    diagnosis_id: string;
    label: string;
    state: string;
  }>;
};

export type DiagnosisRow = {
  id: string;
  code: string;
  label: string;
  sort_order: number;
};

export class CareProtocolApi {
  static async listEntries(caseId: string): Promise<CareEntryView[]> {
    const { isOfflineOnline } = await import("@/offline/networkFlag");
    const { getOfflineStore } = await import("@/offline/store");
    const store = await getOfflineStore();
    const local = await store.getLocalEntries(caseId);
    const localViews: CareEntryView[] = local.map((entry) => ({
      id: entry.id,
      case_id: entry.case_id,
      author_id: entry.author_id ?? "",
      recorded_at: entry.recorded_at,
      received_at: entry.received_at ?? entry.recorded_at,
      is_erroneous: false,
      erroneous_reason: null,
      client_entry_id: entry.client_entry_id,
      events: (entry.events as Array<Record<string, unknown>>) ?? [],
    }));

    if (!isOfflineOnline()) return localViews;

    try {
      const { data, error } = await supabase.rpc("list_care_entries_for_case", {
        p_case_id: caseId,
      });
      if (error) throw error;
      const remote = (data ?? []) as CareEntryView[];
      const pending = local.filter(
        (entry) =>
          entry.syncStatus === "pending" ||
          entry.syncStatus === "conflict" ||
          entry.syncStatus === "quarantine"
      );
      const pendingIds = new Set(pending.map((e) => e.client_entry_id));
      return [
        ...localViews.filter((e) => pendingIds.has(e.client_entry_id ?? "")),
        ...remote.filter((e) => !pendingIds.has(e.client_entry_id ?? "")),
      ];
    } catch {
      return localViews;
    }
  }

  static async getPublicSummary(caseId: string): Promise<PublicCareSummary> {
    const { data, error } = await supabase.rpc("get_case_public_care_summary", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return data as PublicCareSummary;
  }

  static async submitEntryOnline(input: {
    caseId: string;
    clientEntryId: string;
    recordedAt: string;
    events: CareEventInput[];
  }) {
    const { data, error } = await supabase.rpc("submit_care_entry", {
      p_case_id: input.caseId,
      p_client_entry_id: input.clientEntryId,
      p_recorded_at: input.recordedAt,
      p_events: input.events,
    });
    if (error) throw error;
    return data;
  }

  static async submitEntry(input: {
    caseId: string;
    clientEntryId: string;
    recordedAt: string;
    events: CareEventInput[];
  }) {
    const { submitCareEntryViaGateway } = await import(
      "@/offline/sync/gateway"
    );
    return submitCareEntryViaGateway(input);
  }

  static async listDiagnoses(organizationId: string): Promise<DiagnosisRow[]> {
    await supabase.rpc("seed_diagnoses_for_org", { p_org_id: organizationId });
    const { data, error } = await supabase
      .from("diagnoses")
      .select("id, code, label, sort_order")
      .eq("organization_id", organizationId)
      .eq("is_active", true)
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return (data ?? []) as DiagnosisRow[];
  }

  static async canReadFull(caseId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("care_read_full", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return !!data;
  }

  static async canWrite(caseId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("care_write", {
      p_case_id: caseId,
    });
    if (error) throw error;
    return !!data;
  }

  static async createMediaUploadIntent(input: {
    caseId: string;
    clientMediaId: string;
    contentType?: string | null;
  }) {
    const { data, error } = await supabase.rpc("create_media_upload_intent", {
      p_case_id: input.caseId,
      p_client_media_id: input.clientMediaId,
      p_content_type: input.contentType ?? null,
    });
    if (error) throw error;
    return data;
  }

  static async backfillMissingIntakeEntries(): Promise<number> {
    const { data, error } = await supabase.rpc("backfill_missing_intake_entries");
    if (error) throw error;
    return typeof data === "number" ? data : 0;
  }
}
